import {Component, OnInit, OnDestroy, ViewChild} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HhgScopeService } from 'src/app/services/hhg-scope/hhg-scope.service';
import { map, withLatestFrom, tap, takeWhile, find, combineLatest, BehaviorSubject } from 'rxjs';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormArray, UntypedFormControl } from '@angular/forms';
import {StateCountry, RatesTspScope, Carrier} from 'src/app/shared/common-components/common-structure';
import {CarrierApprovalInfo, TransportType} from 'src/app/shared/common-components/AppConstants';
import {CarrierApprovalService} from '../../services/carrier-approval/carrier-approval.service';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {LoadingService} from '../../services/loading/loading.service';
import {AlertService} from '../../services/alert/alert.service';
import {ShipmentAuditTrailService} from '../../services/shipment-audit-trail/shipment-audit-trail.service';
import {ViewInterstateScopeComponent} from '../view-interstate-scope/view-interstate-scope.component';
import {TabsetComponent} from "ngx-bootstrap/tabs";
import {TspScopeComponent} from "../tsp-scope/tsp-scope.component";

@Component({
    selector: 'app-hhg-carrier-tsp-scope',
    templateUrl: './hhg-carrier-tsp-scope.component.html',
    styleUrls: ['./hhg-carrier-tsp-scope.component.css'],
    standalone: false
})
export class HhgCarrierTspScopeComponent implements OnInit, OnDestroy {

  @ViewChild('staticTabs', { static: false }) staticTabs?: TabsetComponent;
  intraStateFromGroup: UntypedFormGroup;
  interStateFromGroup: UntypedFormGroup;
  internationalFromGroup: UntypedFormGroup;
  private observer$ = new BehaviorSubject(true);
  previousSelectedInterStateOriginServiceId = new BehaviorSubject<number>(0);
  previousSelectedInternationalCountryId = new BehaviorSubject<number>(0);
  carrier: Carrier;
  modalRef: BsModalRef;
  hasDomesticApproval: boolean;
  hasInternationalApproval: boolean;
  auditTrailLogList: any;
  isSelectedServiceAreaCanadian = false;

  constructor(private route: ActivatedRoute, private carrierApprovalService: CarrierApprovalService, private modalService: BsModalService,
    private hhgScopeService: HhgScopeService, private formBuilder: UntypedFormBuilder, public loader: LoadingService,
              private alertService: AlertService, private shipmentAuditTrailService: ShipmentAuditTrailService) {
    this.intraStateFromGroup = this.formBuilder.group({
      scopes: this.formBuilder.array([])
    });
    this.interStateFromGroup = this.formBuilder.group({
      originServiceArea: new UntypedFormControl(null),
      scopes: this.formBuilder.array([])
    });
    this.internationalFromGroup = this.formBuilder.group({
      originInternationalCountry: new UntypedFormControl(null),
      isAllStates: new UntypedFormControl(false),
      isAllAlaskaCities: new UntypedFormControl(false),
      isAllCanadianProvinces: new UntypedFormControl(false),
      scopes: this.formBuilder.array([])
    });

  }
  states$ = this.hhgScopeService.states$;
  serviceAreas$ = this.hhgScopeService.serviceAreas$;
  countries$ = this.hhgScopeService.countries$.pipe(
    map(
      (data: any) => {
        data.sort((a: any,b: any) => {
          return a.stateCountryName < b.stateCountryName ? -1 : 1;
        });
        return data.filter(country => country.businessType.toLowerCase().indexOf('hhg') > -1);
      }

    )
  );

  intrastateCarrierScopes$ = this.hhgScopeService.intrastateCarrierScopes$;
  interstateCarrierScopes$ = combineLatest([this.hhgScopeService.interstateCarrierScopes$, this.states$])
    .pipe(
      map(([scopes, states]) => {
        scopes.forEach((interState, i) => {
          interState['originServiceAreaId'] = states.find(s => s.stateCountryId === interState.originId)
            .serviceAreaId;
        });
        return scopes;
      })
    );
  internationalCarrierScopes$ = this.hhgScopeService.internationalCarrierScopes$;
  vm$ = combineLatest([this.states$, this.serviceAreas$, this.countries$, this.internationalCarrierScopes$, this.interstateCarrierScopes$]).pipe(tap(s => console.log(s)));

  intraStateVM$ = combineLatest([this.states$, this.intrastateCarrierScopes$])
    .pipe(map(([states, intraStateScopes]) => {
      states =  states.filter(state => state.stateCountryCode !== 'AK' && state.stateCountryCode !== 'HI' && state.stateCountryCode !== 'PR' && state.parentCountryId !== 365 && state.stateCountryCode2Digit !== 'AK'
        && state.stateCountryCode2Digit !== 'FM');
      const existingScopeIds = intraStateScopes.map(i => i.originId);
      const scopeArray = states.map(state => {
        const originId = existingScopeIds.find(id => id === state.stateCountryId);
        state['scopeId'] = intraStateScopes.find(i => i.originId === originId)
          && intraStateScopes.find(i => i.destinationId === originId).scopeId;
        return this.formBuilder.control(originId);
      });
      this.intraStateFromGroup.setControl('scopes', this.formBuilder.array(scopeArray));
      return states;
    }));

  selectTab(tabId: number) {
    if (this.staticTabs?.tabs[tabId]) {
      this.staticTabs.tabs[tabId].active = true;
    }
  }

  ngOnInit() {
    this.loader.show();
    this.carrierApprovalService.getCarrier(this.route.snapshot.queryParams['carrierId'])
      .pipe(takeWhile(() => this.observer$.value))
      .subscribe(c => {
      this.carrier = c;
      if (this.carrier.carrierApprovalInformationEntity.length) {
        const labels = this.carrier.carrierApprovalInformationEntity.filter(item => !item.revoked).map(item => item.approvalType);
        this.hasDomesticApproval = labels.indexOf(CarrierApprovalInfo.DOMESTICHHG) > -1;
        this.hasInternationalApproval = labels.indexOf(CarrierApprovalInfo.INTERNATIONALHHG) > -1;
      }
        this.loader.hide();
    }, error => {
        this.loader.hide();
        this.alertService.error('An error occurred while getting carrier approval information. Please contact system administrator.')
      });
    this.hhgScopeService.updateCarrierId(this.route.snapshot.queryParams['carrierId']);
    this.interStateFromGroup.get('originServiceArea').valueChanges
      .pipe(
        withLatestFrom(this.states$, this.serviceAreas$, this.interstateCarrierScopes$),
        tap(([originServiceId, states, serviceAreas, existingServiceAreas]) => {
          this.updateInterStateScopes(serviceAreas, states, existingServiceAreas);
          this.previousSelectedInterStateOriginServiceId.next(+originServiceId);
          const selectedExistingServiceAreas = existingServiceAreas.filter(s => s['originServiceAreaId'] === originServiceId);
          const selectedServiceAreaParentCountryId = serviceAreas.find(s => s.stateCountryId === originServiceId).parentCountryId;
          // intra canadian interstate scope is not allowed eg: any origin canadian to destination canadian provinces
          this.isSelectedServiceAreaCanadian = selectedServiceAreaParentCountryId === 365;
          this.interStateFromGroup.setControl('scopes', this.formBuilder.array([]));
          const scopeFormArray = serviceAreas.map(state => {
            const origin = selectedExistingServiceAreas.find(existingServiceArea => existingServiceArea.destinationId
              === state.stateCountryId);
            state['scopeId'] = origin && origin.scopeId;
            return this.formBuilder.control(state['scopeId']);
          });
          this.interStateFromGroup.setControl('scopes', this.formBuilder.array(scopeFormArray));
        })
      ).pipe(takeWhile(() => this.observer$.value)).subscribe();
    this.internationalFromGroup.get('originInternationalCountry').valueChanges
      .pipe(
        withLatestFrom(this.states$, this.internationalCarrierScopes$),
        tap(([originInternationalCountryId, states, existingInternationalScopes]) => {
          this.updateInternationalScopes(states, existingInternationalScopes);
          this.previousSelectedInternationalCountryId.next(+originInternationalCountryId);
          const selectedCountryScopes = existingInternationalScopes.filter(s => s.originId === originInternationalCountryId);
          this.internationalFromGroup.setControl('scopes', this.formBuilder.array([]));
          const scopeFormArray = states.map(state => {
            const origin = selectedCountryScopes.find(existingScope => existingScope.destinationId
              === state.stateCountryId);
            state['scopeId'] = origin && origin.scopeId;
            return this.formBuilder.control(state['scopeId']);
          });
          this.internationalFromGroup.setControl('scopes', this.formBuilder.array(scopeFormArray));
        })
      )
      .pipe(takeWhile(() => this.observer$.value))
      .subscribe();
    this.internationalFromGroup.get('isAllStates').valueChanges
      .pipe(withLatestFrom(this.states$),
        tap(([isSelected, states]) => {
          const existingScopes = this.internationalFromGroup.get('scopes').value;
          const selectedStates = states.map((s, i) => s.contiguousUS === 1 ? isSelected ? true : false : existingScopes[i]);
          this.internationalFromGroup.get('scopes').patchValue(selectedStates);
        })
      ).pipe(takeWhile(() => this.observer$.value)).subscribe();
    this.internationalFromGroup.get('isAllAlaskaCities').valueChanges
      .pipe(withLatestFrom(this.states$),
        tap(([isSelected, states]) => {
          const existingScopes = this.internationalFromGroup.get('scopes').value;
          const selectedStates = states.map((s, i) =>
            s.stateCountryCode2Digit === 'AK' && s.stateCountryCode !== 'AK' ? isSelected ? true : false : existingScopes[i]);
          this.internationalFromGroup.get('scopes').patchValue(selectedStates);
        })
      ).pipe(takeWhile(() => this.observer$.value)).subscribe();
    this.internationalFromGroup.get('isAllCanadianProvinces').valueChanges
      .pipe(withLatestFrom(this.states$, this.countries$),
        tap(([isSelected, states, countries]) => {
          const existingScopes = this.internationalFromGroup.get('scopes').value;
          const canadaStateCountry = countries.find((country: any) => country.stateCountryType === 'Country' && country.stateCountryCode2Digit === 'CA');
          const selectedStates = states.map((s, i) =>
            s.parentCountryId === canadaStateCountry.stateCountryId ? isSelected ? true : false : existingScopes[i]);
          this.internationalFromGroup.get('scopes').patchValue(selectedStates);
        })
      ).pipe(takeWhile(() => this.observer$.value)).subscribe();
  }

  getScopesFormArray() {
    return this.intraStateFromGroup.get('scopes') as UntypedFormArray;
  }

  saveIntraStateScopes(states: StateCountry[]) {
    const intraStateFormValues = this.intraStateFromGroup.getRawValue()['scopes'] as [];
    const deletedIntraStateScopeIds = intraStateFormValues.map((s, i) => {
      if (s === false) {
        return states[i]['scopeId'];
      }
    }).filter(s => s);
    const addedIntraStates = intraStateFormValues.map((s, i) => {
      if (s === true) {
        return states[i];
      }
    }).filter(s => s).map(s => s &&
      new RatesTspScope(this.route.snapshot.queryParams['carrierId'], s.stateCountryId, s.stateCountryId, 'Intrastate'));
    this.hhgScopeService.saveIntraStateScopes(addedIntraStates, deletedIntraStateScopeIds, this.carrier).pipe(takeWhile(() => this.observer$.value))
    .subscribe(t => this.hhgScopeService.updateCarrierId(this.route.snapshot.queryParams['carrierId']));
  }

  saveInterstateScopes(serviceAreas: StateCountry[], states: StateCountry[], existingInterstateScopes: RatesTspScope[]) {
    this.updateInterStateScopes(serviceAreas, states, existingInterstateScopes);
    this.hhgScopeService.saveInterStateScopes(this.carrier).pipe(takeWhile(() => this.observer$.value))
    .subscribe(t => this.hhgScopeService.updateCarrierId(this.route.snapshot.queryParams['carrierId']));
  }

  saveInternationalScopes(states: StateCountry[], existingInternationalScopes: RatesTspScope[]) {
    this.updateInternationalScopes(states, existingInternationalScopes);
    this.hhgScopeService.saveInternationalScopes(this.carrier).pipe(takeWhile(() => this.observer$.value))
    .subscribe(t => this.hhgScopeService.updateCarrierId(this.route.snapshot.queryParams['carrierId']));
  }

  updateInternationalScopes(states: StateCountry[], existingInternationalScopes: RatesTspScope[]) {
    const originCountryId = this.previousSelectedInternationalCountryId.value;
    if (originCountryId <= 0) {
      return;
    }
    const scopes = this.internationalFromGroup.getRawValue()['scopes'] as [];
    if (scopes.filter(s => s === false || s === true).length <= 0) {
      return;
    }
    const deletedInternationalScopeIds = scopes.map((s, i) => {
      if (s === false) {
        return states[i]['scopeId'];
      }
    }).filter(s => s);
    const addedInternationalDestinationStates = scopes.map((s, i) => {
      if (s === true && existingInternationalScopes.findIndex(x => (x.originId === states[i].stateCountryId && x.destinationId === originCountryId)
        || (x.destinationId === states[i].stateCountryId && x.originId === originCountryId)) === -1) {
        return states[i];
      }
    }).filter(s => s);
    const deletedInternationalDestinationStates = scopes.map((s, i) => {
      if (s === false) {
        return states[i];
      }
    }).filter(s => s);
    const addedInternationalScopes = [];
    addedInternationalScopes.push(...addedInternationalDestinationStates.map(s => s &&
      new RatesTspScope(this.route.snapshot.queryParams['carrierId'],
        originCountryId, s.stateCountryId, 'International')));
    addedInternationalScopes.push(...addedInternationalDestinationStates.map(s => s &&
      new RatesTspScope(this.route.snapshot.queryParams['carrierId'],
        s.stateCountryId, originCountryId, 'International')));
    const deletedInternationalScopeIds1 = [];
    deletedInternationalDestinationStates.forEach((dest, i) => {
      existingInternationalScopes.forEach((s, j) => {
        if ((s.destinationId === dest.stateCountryId && s.originId === originCountryId) || (s.originId === dest.stateCountryId && s.destinationId === originCountryId)) {
          deletedInternationalScopeIds1.push(s.scopeId);
        }
      });
    });
    const deletedInternationalScopeIdsFromStates = [...new Set([...deletedInternationalScopeIds, ...deletedInternationalScopeIds1])];
    const existingScopeIdsAfterEdit = existingInternationalScopes.filter(s =>
      states.find(state => (state.stateCountryId === s.destinationId && s.originId === originCountryId) ||
        (state.stateCountryId === s.originId && s.destinationId === originCountryId)) &&
      !deletedInternationalScopeIdsFromStates.includes(s.scopeId));
    if (existingScopeIdsAfterEdit.length <= 0) {
      const countryScopes = existingInternationalScopes.filter(  (s) => {
        if ((s.originId === originCountryId || s.destinationId === originCountryId)
          && !deletedInternationalScopeIdsFromStates.includes(s.scopeId)) {
          return s.scopeId;
        }
      }).map(obj => obj.scopeId);
      deletedInternationalScopeIdsFromStates.push(...countryScopes);
    }
    this.hhgScopeService.deletedCarrierScopes(deletedInternationalScopeIdsFromStates, TransportType.INTERNATIONAL);
    const isNewOriginCountryAdded = existingInternationalScopes.filter(s => s.originId === originCountryId).length <= 0;
    if (isNewOriginCountryAdded) {
      const existingOriginCountryIds = Array.from(new Set(existingInternationalScopes.map(obj => obj.originId))).filter(id => {
        if (states.findIndex(state => state.stateCountryId === id) === -1) {
          return id;
        }
      });
      addedInternationalScopes.push(...existingOriginCountryIds.map(countryId => new RatesTspScope(this.route.snapshot.queryParams['carrierId'],
        countryId, originCountryId, 'International')
      ));
      addedInternationalScopes.push(...existingOriginCountryIds.map(countryId => new RatesTspScope(this.route.snapshot.queryParams['carrierId'],
        originCountryId, countryId, 'International')
      ));
    }
    this.hhgScopeService.modifiedCarrierScopes(addedInternationalScopes, TransportType.INTERNATIONAL);
  }

  updateInterStateScopes(serviceAreas: StateCountry[], states: StateCountry[], existingInterstateScopes: RatesTspScope[]) {
    // get the list of states/services area selected from the dropdown
    const originCountryId = this.previousSelectedInterStateOriginServiceId.value;
    if (originCountryId <= 0) {
      return;
    }
    const originServiceArea = serviceAreas.find(sa => sa.stateCountryId === originCountryId);
    const selectedServiceAreaOriginStates = states.filter(state => state.serviceAreaId === originServiceArea.stateCountryId);

    // if no origin state is selected no need to proceed further; just return
    if (originCountryId <= 0) {
      return;
    }
    // get the scopes from the for array
    const scopes = this.interStateFromGroup.getRawValue()['scopes'] as [];
    // get the list of scopes that are changed either true/false
    if (scopes.filter(s => s === false || s === true).length <= 0) {
      return;
    }
    // get the deleted list of scopes
    const deletedInterstateScopeIds = scopes.map((s, i) => {
      if (s === false) {
        return states[i]['scopeId'];
      }
    }).filter(s => s);
    // get the list of added scopes for the selected dropdown service area
    const addedInterstateDestinationStates = scopes.map((s, i) => {
      if (s === true && existingInterstateScopes.findIndex(x => (x.destinationId === serviceAreas[i].stateCountryId && x.originServiceAreaId === originCountryId)) === -1) {
        return serviceAreas[i];
      }
    }).filter(s => s);
    // get the deleted states values meaning the state object
    const deletedInterstateDestinationStates = scopes.map((s, i) => {
      if (s === false) {
        return serviceAreas[i];
      }
    }).filter(s => s);
    const addedInterstateScopes = [];
    // only make one way record for the list of records
    selectedServiceAreaOriginStates.forEach(originState =>
      addedInterstateScopes.push(...addedInterstateDestinationStates.map(s => s &&
        new RatesTspScope(this.route.snapshot.queryParams['carrierId'], originState.stateCountryId, s.stateCountryId, 'Interstate')))
    );
    const deletedInterstateScopeIds1 = [];
    // remove all the scopes to one destination from multiple service areas
    existingInterstateScopes.forEach(scope => {
      if (
        selectedServiceAreaOriginStates.some(selected => selected.stateCountryId === scope.originId) &&
        deletedInterstateDestinationStates.some(deleted => deleted.stateCountryId === scope.destinationId)
      ) {deletedInterstateScopeIds1.push(scope.scopeId);}
    })
    const deletedInterstateScopeIdsFromStates = [...new Set([...deletedInterstateScopeIds, ...deletedInterstateScopeIds1])];
    // delete the removed scopes
    this.hhgScopeService.deletedCarrierScopes(deletedInterstateScopeIdsFromStates, TransportType.INTERSTATE);
    // add the added scopes
    this.hhgScopeService.modifiedCarrierScopes(addedInterstateScopes, TransportType.INTERSTATE);
  }

  goBack() {
    document.location.href = '/carrierapproval?carrierId=' + this.route.snapshot.queryParams['carrierId'];
  }

  ngOnDestroy(): void {
    this.observer$.next(false);
  }

  viewAllInterstateScope() {
    // TODO integrate backend with
    this.loader.show();
    const profileName = 'Service Area Interstate Scope';
    this.hhgScopeService.getAllScopes(this.route.snapshot.queryParams['carrierId'], 'Interstate').subscribe(data => {
      if (data) {
        const serviceAreaScopeData = data;
        const initialState: any = {
          scopeList: serviceAreaScopeData,
          profile: profileName
        };
        this.modalRef = this.modalService.show(ViewInterstateScopeComponent, { initialState, backdrop: 'static', class: 'modal-xl' });
      } else {
        this.alertService.error('No scopes to display.');
      }
      this.loader.hide();
    }, (error) => {
      this.loader.hide();
      this.alertService.error('There was an error retrieving interstate scope records');
    });
  }

  viewAuditTrial(scopeType: any) {
    this.loader.show();

    // this.profileName = this.carrierAgencyDetails;
    this.hhgScopeService.getAuditTrailListByCarIDAndScopeType(this.carrier.carrierId, scopeType).subscribe(data => {
      //      console.log('auditTrailLogList data: ' + data);
      if (data) {
        this.auditTrailLogList = data;
        console.log('Successfully retrieved audit records And auditTrailLogList length: ' + this.auditTrailLogList.length);
        const initialState: any = {
          auditTrailLogList: this.auditTrailLogList,
          profile: scopeType + 'Audit Trails'
        };
        console.log(this.auditTrailLogList);
        this.modalRef = this.modalService.show(TspScopeComponent, { initialState, backdrop: 'static', class: 'modal-xl' });

      } else {
        this.alertService.error('No Audit Trail to display.');
      }
      this.loader.hide();
    }, (error) => {
      console.log('There was an error retrieving auditTrailLog records');
      this.loader.hide();
      this.alertService.error('There was an error retrieving auditTrailLog records');
    });

  }
}
